import { ref } from 'vue';
import { useGameStore } from '@/stores/game';

export interface RelatedGame {
  slug: string;
  name: string;
  id: number;
  logo?: string | null;
}

const relatedGamesCache = ref<Record<string, RelatedGame[]>>({});

export function useRelatedGames() {
  const gameStore = useGameStore();

  const getRelatedGames = async (gameSlug: string): Promise<RelatedGame[]> => {
    // Check cache first
    if (relatedGamesCache.value[gameSlug]) {
      return relatedGamesCache.value[gameSlug];
    }

    try {
      // Fetch game details which includes related_games
      await gameStore.fetchGameDetails(gameSlug);
      
      // Get related games from the game details response
      const gameDetails = gameStore.gameDetails;
      const relatedGames = gameDetails?.related_games || [];
      
      // Cache the result
      relatedGamesCache.value[gameSlug] = relatedGames;
      
      return relatedGames;
    } catch (error) {
      console.error('Error fetching related games:', error);
      // Return empty array on error
      return [];
    }
  };

  const getRelatedGamesSync = (gameSlug: string): RelatedGame[] => {
    // Check cache first
    if (relatedGamesCache.value[gameSlug]) {
      return relatedGamesCache.value[gameSlug];
    }

    // Check if game details are already loaded
    const gameDetails = gameStore.gameDetails;
    if (gameDetails?.game?.slug === gameSlug && gameDetails.related_games) {
      relatedGamesCache.value[gameSlug] = gameDetails.related_games;
      return gameDetails.related_games;
    }

    return [];
  };

  const hasRelatedGames = (gameSlug: string): boolean => {
    const relatedGames = getRelatedGamesSync(gameSlug);
    return relatedGames.length > 0;
  };

  const clearCache = (gameSlug?: string): void => {
    if (gameSlug) {
      delete relatedGamesCache.value[gameSlug];
    } else {
      relatedGamesCache.value = {};
    }
  };

  const getGameDisplayName = (gameSlug: string): string => {
    const relatedGames = getRelatedGamesSync(gameSlug);
    const game = relatedGames.find(g => g.slug === gameSlug);
    if (game) {
      return game.name;
    }
    
    // Fallback: format slug
    return gameSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return {
    getRelatedGames,
    getRelatedGamesSync,
    hasRelatedGames,
    clearCache,
    getGameDisplayName,
  };
}

